import AsyncStorage from "@react-native-async-storage/async-storage";
import React, { useEffect, useState } from "react";
import { Alert, Pressable, Text, View } from "react-native";
import { CheckCircle, Copy, Download, Edit3, Eye, FileText, Plus, Receipt, RotateCcw, Search, Send, Trash2, XCircle } from "lucide-react-native";
import { api } from "../api/client";
import { isAdmin as checkAdmin, isManager as checkManager } from "../utils/format";
import { ActionMenu, Badge, Button, Card, DataTable, Field, IconButton, ScreenHeader, Select, Toolbar } from "../components/UI";
import { styles } from "../styles";

export function QuotationsScreen({ user, onNew, onEdit, onViewInvoice, onGenerateInvoice }) {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [status, setStatus] = useState("");
  const isAdmin = checkAdmin(user);
  const isManager = checkManager(user);
  const load = () => api.quotations(q, dateFrom, dateTo, status).then(setRows).catch((error) => Alert.alert("Quotations", error.message));
  
  useEffect(() => {
    load();
  }, []);

  const duplicate = async (id) => {
    await api.duplicateQuotation(id);
    load();
  };

  const remove = async (id) => {
    if (confirm("Delete this quotation?")) {
      await api.deleteQuotation(id);
      load();
    }
  };

  const download = async (id, type = "final") => {
    const token = await AsyncStorage.getItem("auth_token");
    if (typeof window !== "undefined") window.open(`${api.pdfUrl(id)}?type=${type}&token=${token}`, "_blank");
  };

  const generateInvoice = async (quotationId) => {
    if (onGenerateInvoice) {
      onGenerateInvoice(quotationId);
      return;
    }
    try {
      const inv = await api.createInvoiceFromQuotation(quotationId);
      Alert.alert("Invoice Generated", `Invoice ${inv.invoice_number} created successfully.`);
      load();
      if (onViewInvoice) onViewInvoice(inv.id);
    } catch (error) {
      if (error.invoice_id && onViewInvoice) {
        onViewInvoice(error.invoice_id);
      } else {
        Alert.alert("Generate Invoice", error.message);
      }
    }
  };

  const action = async (fn, success) => {
    try {
      await fn();
      Alert.alert("Quotation", success);
      load();
    } catch (error) {
      Alert.alert("Quotation", error.message);
    }
  };

  const submit = (id) => action(() => api.submitQuotation(id), "Quotation submitted for approval.");
  const approve = (id) => action(() => api.approveQuotation(id, prompt("Approval comments (optional)") || ""), "Quotation approved.");
  const reject = (id) => {
    const reason = prompt("Rejection reason");
    if (reason) action(() => api.rejectQuotation(id, reason), "Quotation rejected.");
  };
  const returnForRevision = (id) => {
    const comments = prompt("Revision comments");
    if (comments) action(() => api.returnQuotation(id, comments), "Quotation returned for revision.");
  };

  return (
    <View style={styles.page}>
      <ScreenHeader title="Quotations" subtitle="Search, edit, duplicate, delete, and export saved quotations">
        {(isAdmin || isManager) ? <Button icon={Plus} onPress={onNew}>New Quotation</Button> : null}
      </ScreenHeader>

      {(isAdmin || isManager) ? (
        <Text style={[styles.message, styles.successMessage]}>
          Draft PDF is for internal review only and is not valid for customer submission.
        </Text>
      ) : null}

      <Card style={{ padding: 16 }}>
        <Toolbar>
          <Field label="Search Query" value={q} onChangeText={setQ} placeholder="Quotation No, Client, Project..." style={{ flex: 2 }} />
          <Field label="Date From" value={dateFrom} onChangeText={setDateFrom} placeholder="YYYY-MM-DD" />
          <Field label="Date To" value={dateTo} onChangeText={setDateTo} placeholder="YYYY-MM-DD" />
          <Select label="Filter Status" value={status} onChangeText={setStatus}>
            <option value="">All Statuses</option>
            <option value="draft">Draft</option>
            <option value="submitted">Submitted</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
            <option value="revision_required">Revision Required</option>
          </Select>
          <Button icon={Search} onPress={load}>Filter</Button>
        </Toolbar>
      </Card>

      <DataTable
        actionsWidth={56}
        columns={[
          {
            key: "quotation_number",
            label: "Quotation No",
            flex: 1.3,
            render: (r) => (
              <View style={{ flexDirection: "row", alignItems: "center", gap: 6 }}>
                <Text style={{ fontWeight: "700" }}>{r.quotation_number}</Text>
                {Number(r.revision_number || 0) > 0 ? (
                  <Badge variant="warning">Rev {r.revision_number}</Badge>
                ) : null}
              </View>
            )
          },
          { key: "client_name", label: "Client", flex: 1.4 },
          { key: "project_title", label: "Project Title", flex: 1.5 },
          {
            key: "grand_total",
            label: "Grand Total",
            flex: 1.3,
            render: (r) => `PKR ${Number(r.grand_total || 0).toLocaleString("en-PK", { minimumFractionDigits: 2 })}`
          },
          { key: "created_by_name", label: "Created By", flex: 1.1, render: (r) => r.created_by_name || "-" },
          {
            key: "status",
            label: "Status",
            flex: 0.9,
            render: (r) => (
              <Badge variant={r.status === "approved" ? "success" : r.status === "rejected" ? "danger" : "pending"}>
                {statusLabel(r.status)}
              </Badge>
            )
          },
          {
            key: "invoice_number",
            label: "Invoice",
            flex: 0.8,
            render: (r) =>
              r.invoice_id ? (
                <Pressable onPress={() => onViewInvoice && onViewInvoice(r.invoice_id)}>
                  <Badge variant={r.invoice_status === "paid" ? "success" : "info"}>
                    {r.invoice_number}
                  </Badge>
                </Pressable>
              ) : (
                "-"
              )
          },
          {
            key: "submitted_at",
            label: "Submitted",
            flex: 0.9,
            render: (r) => (r.submitted_at ? String(r.submitted_at).slice(0, 10) : "-")
          },
          { key: "approved_by_name", label: "Approved By", flex: 1.1, render: (r) => r.approved_by_name || "-" }
        ]}
        rows={rows}
        renderActions={(row) => {
          const isOwner = Number(row.created_by_user_id) === Number(user?.id);
          const canCrud = isAdmin || (isManager && isOwner);

          return (
            <ActionMenu
              actions={[
                { label: canCrud ? "Edit Quotation" : "View Quotation", icon: canCrud ? Edit3 : Eye, onPress: () => onEdit(row.id) },
                canCrud && canSubmit(row.status) && { label: "Submit for Approval", icon: Send, variant: "primary", onPress: () => submit(row.id) },
                isAdmin && String(row.status).toLowerCase() === "submitted" && { label: "Approve Quotation", icon: CheckCircle, variant: "success", onPress: () => approve(row.id) },
                isAdmin && String(row.status).toLowerCase() === "submitted" && { label: "Reject Quotation", icon: XCircle, variant: "danger", onPress: () => reject(row.id) },
                isAdmin && String(row.status).toLowerCase() === "submitted" && { label: "Return for Revision", icon: RotateCcw, onPress: () => returnForRevision(row.id) },
                (isAdmin || (isManager && isOwner)) && canDownloadFinal(row.status) && !row.invoice_id && { label: "Generate Invoice", icon: Receipt, variant: "primary", onPress: () => generateInvoice(row.id) },
                (isAdmin || (isManager && isOwner)) && canDownloadFinal(row.status) && row.invoice_id && { label: "View Invoice", icon: Receipt, onPress: () => onViewInvoice && onViewInvoice(row.invoice_id) },
                canCrud && { label: "Duplicate Quotation", icon: Copy, onPress: () => duplicate(row.id) },
                (isAdmin || (isManager && isOwner)) && canDownloadDraft(row.status) && { label: "Draft PDF (Review)", icon: FileText, onPress: () => download(row.id, "draft") },
                (isAdmin || (isManager && isOwner)) && canDownloadFinal(row.status) && { label: "Final Official PDF", icon: Download, onPress: () => download(row.id, "final") },
                canCrud && { label: "Delete Quotation", icon: Trash2, variant: "danger", onPress: () => remove(row.id) }
              ]}
            />
          );
        }}
      />
      <Text style={styles.small}>PDF export is available now. Excel export is prepared as a future API extension.</Text>
    </View>
  );
}

function statusLabel(status) {
  const value = String(status || "draft").replace("_", " ");
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function canSubmit(status) {
  return ["draft", "revision_required", "rejected"].includes(String(status || "draft").toLowerCase());
}

function canDownloadDraft(status) {
  return ["draft", "submitted", "pending_approval", "rejected", "revision_required", "needs_revision"].includes(String(status || "draft").toLowerCase());
}

function canDownloadFinal(status) {
  return ["approved", "finalized", "sent"].includes(String(status || "").toLowerCase());
}
